import React, { FC, useEffect, useState } from "react";
import Input from "shared/Input/Input";
import FormItem from "./FormItem";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  updateProperties,
  getallupdateProperties,
} from "../../redux/reducers/Properties/PropertiesSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { PricevalidationSchemas } from "utils/formSchema";
import CheckboxV2 from "shared/Checkbox/CheckboxV2";
import CommonLayoutV2 from "./CommonLayoutV2";
import { currencySymbol } from "shared/CurrencySymbol";
import successHandler from "utils/helpers/SuccessHandler";
import {
  calculateStepCompletion,
  checkExistingCompletion,
} from "utils/helpers/listingCompletionHelper";

export interface PriceProps {}

interface InitialValues {
  price_per_month: string;
  taxes_in_percentage: string;
  cleaning_fee: string;
  city_fee: string;
  is_city_fee_percentage: boolean;
  minimum_months_of_booking: string;
  security_deposit: string;
  security_deposit_details: string;  // ADD THIS LINE
  security_deposit_text: string;         // ADD THIS LINE
  early_bird_discount: string;
  advance_days: string;
  per_extra_guest_amount: string;
  allow_extra_guest: boolean;
  isSharedProperty: boolean;
}

const Price: FC<PriceProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { globalId, PropertyToEdit } = useSelector(
    (state: RootState) => state.property
  );
  const { slug } = useParams();
  const id = slug;
  const RouteID = id;

  const [initialValues, setInitialValues] = useState({
    price_per_month: "",
    taxes_in_percentage: "",
    cleaning_fee: "",
    city_fee: "",
    is_city_fee_percentage: false,
    minimum_months_of_booking: "",
    security_deposit: "",
    security_deposit_details: "", 
    security_deposit_text: "",         // ADD THIS LINE
    early_bird_discount: "",
    advance_days: "",
    per_extra_guest_amount: "",
    allow_extra_guest: false,
    isSharedProperty: false,
  });

  const [cachedPropertyType, setCachedPropertyType] = useState<string | null>(null);

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false, fieldsCompleted: 0, totalFields: 4 },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false },
    9: { completed: false },
  });

  useEffect(() => {
    if (globalId || id) {
      const fetchPropertyData = async () => {
        const response: any = await dispatch(
          getallupdateProperties({ propertyId: id })
        );
        const property = response.payload?.property;
        if (property) {
          setInitialValues({
            price_per_month: property.price_per_month || "",
            taxes_in_percentage: property.taxes_in_percentage || "",
            cleaning_fee: property.cleaning_fee,
            city_fee: property.city_fee,
            is_city_fee_percentage: property.is_city_fee_percentage || false,
            minimum_months_of_booking: property.minimum_months_of_booking,
            security_deposit: property.security_deposit,
            security_deposit_details: property.security_deposit_details || "", 
            security_deposit_text: property.security_deposit_text || "", 
            early_bird_discount: property.early_bird_discount,
            advance_days: property.advance_days,
            per_extra_guest_amount: property.per_extra_guest_amount,
            allow_extra_guest: property.allow_extra_guest,
            isSharedProperty: property.is_shared_property,
          });
        } else {
          console.error("Property not found:", property);
        }
      };

      fetchPropertyData();
    }
  }, [dispatch, globalId]);

  const isDirty = (
    values: typeof initialValues,
    initialValues: InitialValues
  ) =>
    (Object.keys(values) as (keyof InitialValues)[]).some(
      (key) => values[key] !== initialValues[key]
    );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: PricevalidationSchemas,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const dirty = isDirty(values, initialValues);
      const payload = {
        id: id,
        price_per_month: values.price_per_month,
        taxes_in_percentage: values.taxes_in_percentage,
        cleaning_fee: values.cleaning_fee,
        city_fee: values.city_fee,
        is_city_fee_percentage: values.is_city_fee_percentage,
        minimum_months_of_booking: values.minimum_months_of_booking,
        security_deposit: values.security_deposit,
        security_deposit_details: values.security_deposit_details,  // ADD THIS LINE
        security_deposit_text: values.security_deposit_text,    // ADD THIS LINE
        early_bird_discount: values.early_bird_discount,
        advance_days: values.advance_days,
        per_extra_guest_amount: values.allow_extra_guest
          ? values.per_extra_guest_amount
          : undefined,
        allow_extra_guest: values.allow_extra_guest,
      };

      setSubmitting(true);
      if (dirty) {
        dispatch(updateProperties({ property: payload }))
          .unwrap()
          .then((response: any) => {
            if (response) {
              successHandler(response?.message || "Updated successfully");
              resetForm();
              navigate(`/add-listing-3/${slug}`);
            }
          })
          .catch((error: any) => {
            console.error("Error updating property: ", error);
          })
          .finally(() => {
            setSubmitting(false);
          });
      } else {
        navigate(`/add-listing-3/${slug}`);
      }
    },
  });

  // Calculate completion for Step 2 (Price) using helper
  useEffect(() => {
    const requiredFields = initialValues.isSharedProperty
      ? [
          "cleaning_fee",
          "city_fee",
          "minimum_months_of_booking",
          "security_deposit",
        ]
      : [
          "price_per_month",
          "cleaning_fee",
          "city_fee",
          "minimum_months_of_booking",
        ];

    const hasErrors = Object.keys(formik.errors).length > 0;
    const completion = calculateStepCompletion(
      formik.values,
      requiredFields,
      hasErrors
    );

    setStepCompletionStatus((prev) => ({
      ...prev,
      2: completion,
    }));
  }, [formik.values, formik.errors, initialValues.isSharedProperty]);

  // Load completion status from PropertyToEdit using helper
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);

      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  // ADD THIS: Update cached property type when PropertyToEdit loads
  useEffect(() => {
    if (PropertyToEdit?.property_type?.name) {
      setCachedPropertyType(PropertyToEdit.property_type.name);
    }
  }, [PropertyToEdit?.property_type?.name]);

  return (
    <CommonLayoutV2
      currentHref="/add-listing-2"
      PropertyID={RouteID}
       propertyType={cachedPropertyType} 
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Set Your Pricing
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {initialValues.isSharedProperty ? (
              <>
                Configure fees and policies for your shared property.
                Room-specific pricing will be set at the room level in the next
                steps.
              </>
            ) : (
              <>
                Configure your pricing structure and fees. Your revenue depends
                on competitive rates and clear policies.
              </>
            )}
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-8">
          {/* Base Pricing Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                1
              </span>
              Base Pricing
            </h3>

            <div className="space-y-6">
              {!initialValues.isSharedProperty && (
                <FormItem
                  label="Monthly Rate *"
                  desc="Set your base monthly rental price in USD."
                >
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 dark:text-gray-400 font-medium">
                      {currencySymbol()}
                    </span>
                    <Input
                      name="price_per_month"
                      type="text"
                      placeholder="1,500"
                      className="pl-10 text-base"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.price_per_month}
                    />
                  </div>
                  {formik.errors.price_per_month &&
                    formik.touched.price_per_month && (
                      <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {formik.errors.price_per_month}
                      </div>
                    )}
                </FormItem>
              )}

              <FormItem
                label="Minimum Booking Duration *"
                desc="Set the minimum number of months required for booking."
              >
                <div className="relative">
                  <Input
                    name="minimum_months_of_booking"
                    type="number"
                    value={formik.values.minimum_months_of_booking}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="1"
                    className="text-base"
                  />
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 dark:text-gray-400">
                    {formik.values.minimum_months_of_booking === "1"
                      ? "month"
                      : "months"}
                  </span>
                </div>
                {formik.touched.minimum_months_of_booking &&
                  formik.errors.minimum_months_of_booking && (
                    <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {formik.errors.minimum_months_of_booking}
                    </div>
                  )}
              </FormItem>
            </div>
          </div>

          {/* Additional Fees Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
                2
              </span>
              Additional Fees
            </h3>

            <div className="space-y-6">
              <FormItem
                label="Cleaning Fee"
                desc="One-time fee charged for cleaning services."
              >
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 dark:text-gray-400 font-medium">
                    {currencySymbol()}
                  </span>
                  <Input
                    name="cleaning_fee"
                    type="text"
                    value={formik.values.cleaning_fee}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="150"
                    className="pl-10 text-base"
                  />
                </div>
                {formik.touched.cleaning_fee && formik.errors.cleaning_fee && (
                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {formik.errors.cleaning_fee}
                  </div>
                )}
              </FormItem>

              <FormItem
                label="City Fee (per month)"
                desc="Monthly city or local government fee."
              >
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 dark:text-gray-400 font-medium">
                    {currencySymbol()}
                  </span>
                  <Input
                    name="city_fee"
                    type="number"
                    value={formik.values.city_fee}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="50"
                    className="pl-10 text-base"
                  />
                </div>
                {formik.touched.city_fee && formik.errors.city_fee && (
                  <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {formik.errors.city_fee}
                  </div>
                )}
              </FormItem>
            </div>
          </div>

         {/* Security & Deposits Card */}
<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
    <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold">
      3
    </span>
    Security & Deposits
  </h3>

  <div className="space-y-6">
   <FormItem
  label="Security Deposit"
  desc="Enter deposit amount or custom text (e.g., '500', or '$500 refundable')."
>
  <textarea
    name="security_deposit_text"
    value={formik.values.security_deposit_text}
    onChange={formik.handleChange}
    onBlur={formik.handleBlur}
    placeholder="Example: $500 refundable deposit or 'Contact host for pricing details'"
    rows={3}
    className="block w-full border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900 rounded-2xl text-sm font-normal h-auto px-4 py-3"
  />
  {formik.touched.security_deposit_text &&
    formik.errors.security_deposit_text && (
      <div className="text-red-500 text-sm mt-2">
        {formik.errors.security_deposit_text}
      </div>
    )}
</FormItem>

    {/* ADD THIS NEW FIELD HERE */}
   <FormItem
  label="Security Deposit Details"
  desc="Provide additional information about your security deposit policy (e.g., refund conditions, timeline, deductions)."
>
  <textarea
    name="security_deposit_details"
    value={formik.values.security_deposit_details}
    onChange={formik.handleChange}
    onBlur={formik.handleBlur}
    placeholder="Example: Security deposit is fully refundable within 14 days after checkout if the property is left in good condition with no damages..."
    rows={4}
    className="block w-full border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900 rounded-2xl text-sm font-normal h-auto px-4 py-3"
  />
  {formik.touched.security_deposit_details &&
    formik.errors.security_deposit_details && (
      <div className="text-red-500 text-sm mt-2">
        {formik.errors.security_deposit_details}
      </div>
    )}
</FormItem>
    {/* END OF NEW FIELD */}

  </div>
</div>

          {/* Guest Policies Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-bold">
                4
              </span>
              Guest Policies
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                (Optional)
              </span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 ml-10">
              Configure additional charges for guests beyond your property's
              capacity.
            </p>

            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border border-gray-200 dark:border-gray-600">
                <CheckboxV2
                  name="allow_extra_guest"
                  label="Allow guests above capacity"
                  onChange={(checked) =>
                    formik.setFieldValue("allow_extra_guest", checked)
                  }
                  checked={formik.values.allow_extra_guest}
                />
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 ml-6">
                  Enable this to allow bookings with more guests than your
                  property's standard capacity.
                </p>
              </div>

              {formik.values.allow_extra_guest && (
                <div className="ml-6 pl-6 border-l-2 border-[#F56948]">
                  <FormItem
                    label="Extra Guest Fee (per guest, per month)"
                    desc="Additional charge for each guest beyond capacity."
                  >
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 dark:text-gray-400 font-medium">
                        {currencySymbol()}
                      </span>
                      <Input
                        name="per_extra_guest_amount"
                        type="number"
                        value={formik.values.per_extra_guest_amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d*$/.test(value)) {
                            formik.handleChange(e);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        placeholder="100"
                        className="pl-10 text-base"
                        disabled={!formik.values.allow_extra_guest}
                      />
                    </div>
                    {formik.touched.per_extra_guest_amount &&
                      formik.errors.per_extra_guest_amount && (
                        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {formik.errors.per_extra_guest_amount}
                        </div>
                      )}
                  </FormItem>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Step 2 of 9 • Configure your pricing structure
            </div>
            <div className="flex gap-3">
              <ButtonSecondary
                href={`/add-listing-1/${id}`}
                disabled={formik.isSubmitting}
              >
                <span className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Go Back
                </span>
              </ButtonSecondary>
              <ButtonPrimary
                type="submit"
                disabled={formik.isSubmitting}
                className="min-w-[140px]"
              >
                {formik.isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continue
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </span>
                )}
              </ButtonPrimary>
            </div>
          </div>
        </form>
      </div>
    </CommonLayoutV2>
  );
};

export default Price;
